<template>
	<div class="card-component">
		<div v-if="mostrarNombresFichero">
			<p class="nombre-fichero">{{ `CFG, DAT: ${fileName}` }}</p>
		</div>
		<div class="datos-senales">
			<DxToolbar>
				<DxItem location="before">
					<DxButtonGroup :items="[btnOptions.primary_vals, btnOptions.secondary_vals]" keyExpr="groupItemKey"
						v-model:selectedItemKeys="activeButtonsByGroup.channels"
						@initialized="(e: InitializedEvent) => groupRefs.channels = e.component!">
					</DxButtonGroup>
				</DxItem>
				<DxItem location="before">
					<DxButtonGroup :items="[btnOptions.only_changed, btnOptions.vector]" selection-mode="multiple"
						keyExpr="groupItemKey" v-model:selectedItemKeys="activeButtonsByGroup.visualizations"
						@initialized="(e: InitializedEvent) => groupRefs.visualizations = e.component!">
					</DxButtonGroup>
				</DxItem>
				<DxItem location="before">
					<DxButtonGroup :items="[btnOptions.spectr, btnOptions.freq, btnOptions.sym, btnOptions.calc]"
						selection-mode="none"
						@initialized="(e: InitializedEvent) => groupRefs.calculated = e.component!">
					</DxButtonGroup>
				</DxItem>
				<DxItem location="before">
					<DxButtonGroup :items="[btnOptions.zoom_button, btnOptions.pan_button]" keyExpr="groupItemKey"
						@initialized="(e: InitializedEvent) => groupRefs.tools = e.component!">
					</DxButtonGroup>
				</DxItem>
				<DxItem location="before">
					<DxButtonGroup :items="[btnOptions.reset_button, btnOptions.csv, btnOptions.settings]"
						selection-mode="none" @initialized="(e: InitializedEvent) => groupRefs.actions = e.component!">
					</DxButtonGroup>
				</DxItem>
				<DxItem location="after" :caption="$t('ficheros_medida.grid.toolbar.descargar_ficheros_medida')"
					template="btnDescargarFicherosMedida" />
				<template #btnDescargarFicherosMedida>
					<DxButton :hint="$t('ficheros_medida.grid.toolbar.descargar_ficheros_medida')"
						icon="fa-solid fa-download" styling-mode="text" type="default"
						@click="descargarFicherosMedida" />
				</template>
			</DxToolbar>


			<!-- TODO RUBÉN: FUTURO: CAMBIAR ESTE "grid" POR ALGO MAS SENSATO -->
			<div id="grid" class="grid" v-once>
				<div id="info" class="div_info"></div>
				<div id="osc_place" style="margin-left: -10px !important;"></div>
				<div id="tools">
					<div id="osc_tool" class="div_osc_tool"></div>
					<div id="osc_tool_data" class="div_osc_tool"></div>
				</div>
			</div>

		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { DxItem } from 'devextreme-vue/toolbar';
import SettingsModal from './SettingsModal.vue';
import type dxButton from 'devextreme/ui/button';
import { useIntl } from '@/.shared/services/i18n';
import { useApi } from '@/.shared/.api/integration';
import type dxButtonGroup from 'devextreme/ui/button_group';
import CalculatedSignalModal from './CalculatedSignalModal.vue';
import FrequencySpectrumModal from './FrequencySpectrumModal.vue';
import type { InitializedEvent } from 'devextreme/ui/button_group';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useModalService } from '@/.shared/services/modal-service';
import { DxButtonGroup, DxToolbar, DxButton } from 'devextreme-vue';
import SymmetricalComponentsModal from './SymmetricalComponentsModal.vue';
import { getNombreFicheroSinExtension } from '@/.shared/helpers/file-helper';
import FrequencySignalSelectorModal from './FrequencySignalSelectorModal.vue';
import type { messageBoxIcon } from '@/.shared/components/dialogs/message-box';
import { hideLoadingPanel, showLoadingPanel } from '@/.shared/services/loading-service';
import Osc, { type AvailableButtons, type CalculatedSignalDialogResult, type FrequencyDialogResult, type SettingsDialogResult, type SymmetricalDialogResult } from "@/components/senyales/osc";
import type { ClickEvent } from 'devextreme/ui/button';
import { descargarZipFicherosMedida } from '@/.shared/helpers/fichero-medida-helper';

const api = useApi();
const i18n = useIntl();
const route = useRoute();
const props = defineProps<{ files: { cfgFile: File, datFile: File } | null, mostrarNombresFichero: boolean }>();
// const cfgFileName = computed(() => props.files?.cfgFile.name);
// const datFileName = computed(() => props.files?.datFile.name);
let fileName = ref<string>('');

const activeButtonsByGroup = {
	channels: [] as string[],
	visualizations: [] as string[],
	calculated: [] as string[],
	tools: [] as string[],
	actions: [] as string[],
};
const modal = useModalService();
let loadingId: string;

const btnOptions: Record<AvailableButtons, DxButton & { groupItemKey?: string, group: keyof typeof activeButtonsByGroup }> = {
	"primary_vals": {
		groupItemKey: 'primary_vals',
		group: "channels",
		hint: i18n.i18n.t('signal_viewer.btn_options.primary_vals.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/primary.png',
		onClick: () => {
			Osc.disp_primary_values(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['primary_vals'] = e.component!;
		}
	},
	"secondary_vals": {
		groupItemKey: 'secondary_vals',
		group: "channels",
		hint: i18n.i18n.t('signal_viewer.btn_options.secondary_vals.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/secondary.png',
		onClick: () => {
			Osc.disp_secondary_values(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['secondary_vals'] = e.component!;
		}
	},
	"only_changed": {
		groupItemKey: 'only_changed',
		group: "visualizations",
		hint: i18n.i18n.t('signal_viewer.btn_options.only_changed.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/change.png',
		onClick: () => {
			Osc.disp_change(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['only_changed'] = e.component!;
		}
	},
	"vector": {
		groupItemKey: 'vector',
		group: "visualizations",
		hint: i18n.i18n.t('signal_viewer.btn_options.vector.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/vector.png',
		onClick: () => {
			Osc.disp_vector(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['vector'] = e.component!;
		}
	},
	"spectr": {
		groupItemKey: 'spectr',
		group: "calculated",
		hint: i18n.i18n.t('signal_viewer.btn_options.spectr.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/spectrum.png',
		onClick: async () => {
			await openSpectrumDialog();
		},
		onInitialized: (e) => {
			btnRefs['spectr'] = e.component!;
		}
	},
	"sym": {
		groupItemKey: 'sym',
		group: "calculated",
		hint: i18n.i18n.t('signal_viewer.btn_options.sym.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/sym.png',
		onClick: () => {
			Osc.disp_sym(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['sym'] = e.component!;
		}
	},
	"freq": {
		groupItemKey: 'freq',
		group: "calculated",
		hint: i18n.i18n.t('signal_viewer.btn_options.freq.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/freq.png',
		onClick: () => {
			Osc.disp_freq(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['freq'] = e.component!;
		}
	},
	"calc": {
		groupItemKey: 'calc',
		group: "calculated",
		hint: i18n.i18n.t('signal_viewer.btn_options.calc.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/calc.png',
		onClick: () => {
			Osc.disp_calc(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	},
	"zoom_button": {
		groupItemKey: 'zoom_button',
		group: "tools",
		hint: i18n.i18n.t('signal_viewer.btn_options.zoom_button.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/zoom.png',
		onClick: () => {
			Osc.zoom_axes();
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	},
	"pan_button": {
		groupItemKey: 'pan_button',
		group: "tools",
		hint: i18n.i18n.t('signal_viewer.btn_options.pan_button.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/pan.png',
		onClick: () => {
			Osc.pan_axes();
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	},
	"reset_button": {
		groupItemKey: 'reset_button',
		group: "actions",
		hint: i18n.i18n.t('signal_viewer.btn_options.reset_button.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/home.png',
		onClick: () => {
			Osc.reset_axes();
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	},
	"csv": {
		groupItemKey: 'csv',
		group: "actions",
		hint: i18n.i18n.t('signal_viewer.btn_options.csv.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/csv.png',
		onClick: () => {
			Osc.save_csv(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	},
	"settings": {
		groupItemKey: 'settings',
		group: "actions",
		hint: i18n.i18n.t('signal_viewer.btn_options.settings.hint'),
		icon: import.meta.env.VITE_BASE_URL + '/img/osc/settings.png',
		onClick: () => {
			Osc.settings(Osc.getOscData());
		},
		onInitialized: (e) => {
			btnRefs['calc'] = e.component!;
		}
	}
};

const id = ref<number>();
const btnRefs = {} as Record<keyof typeof btnOptions, dxButton>;
const groupRefs = {} as Record<keyof typeof activeButtonsByGroup, dxButtonGroup>;

let loadDelayed = false;
let mounted = false;

watch(
	() => props.files,
	(newFiles: { cfgFile: File, datFile: File } | null) => {
		if (newFiles && newFiles.cfgFile && newFiles.datFile) {
			if (mounted) {
				fileName.value = getNombreFicheroSinExtension(newFiles.cfgFile.name)!;
				loadFiles([newFiles.cfgFile, newFiles.datFile]);
			}
			else {
				loadDelayed = true;
			}
		}

	}, { immediate: true });

onMounted(() => {
	Osc.osc_set_methods({
		enableButton: enableToolbarButton,
		disableButton: disableToolbarButton,
		setOnClick: setToolbarButtonOnclick,
		setActive: setToolbarButtonActive,
		isActive: getToolbarButtonActive,
		openFrequencyDialog: openFrequencyDialog,
		openSymmetricalDialog: openSymmetricalDialog,
		openCalculatedDialog: openCalculatedDialog,
		openSettingsDialog: openSettingsDialog,
		showSpinner: showSpinner,
		hideSpinner: hideSpinner,
		alert: showAlert
	});

	if (loadDelayed && props.files) {
		loadFiles([props.files.cfgFile, props.files.datFile]);
		loadDelayed = false;
	}

	watch(
		() => route.params.id,
		async (newId: string | string[]) => {
			id.value = parseInt(newId as string);
		},
		{ immediate: true }
	);

	mounted = true;
});

onUnmounted(() => {
	Osc.osc_set_methods(null);
	mounted = false;
});

function loadFiles(files: File[]) {
	const loadingId = showLoadingPanel();
	window.setTimeout(() => {
		Osc.load_osc_files(files);
		hideLoadingPanel(loadingId);
	}, 100);
}

function enableToolbarButton(buttonId: AvailableButtons) {
	const button = btnRefs[buttonId];
	if (button) {
		button.option('disabled', false);
	}
}

function disableToolbarButton(buttonId: AvailableButtons) {
	const button = btnRefs[buttonId];
	if (button) {
		button.option('disabled', true);
	}
}

function setToolbarButtonOnclick(buttonId: AvailableButtons, onclick: () => void) {
	const button = btnRefs[buttonId];
	if (button) {
		button.option('onClick', onclick);
	}
}

function getToolbarButtonActive(buttonId: AvailableButtons) {
	const group = btnOptions[buttonId].group;
	const activeButtonsList = activeButtonsByGroup[group];
	return activeButtonsList.includes(buttonId);
}

function setToolbarButtonActive(buttonId: AvailableButtons, active: boolean) {
	const group = btnOptions[buttonId].group;
	const activeButtonsList = activeButtonsByGroup[group];

	if (active) {
		if (!activeButtonsList.includes(buttonId)) {
			activeButtonsList.push(buttonId);
		}
	} else {
		const index = activeButtonsList.indexOf(buttonId);
		if (index >= 0) {
			activeButtonsList.splice(index, 1);
		}
	}

	groupRefs[group]?.option('selectedItemKeys', activeButtonsList);
}

async function openFrequencyDialog(signals: string[]): Promise<FrequencyDialogResult> {
	const result = await modal.showModal(FrequencySignalSelectorModal, { signals: signals }, { width: 500, height: 300, title: i18n.i18n.t('signal_viewer.dialogs.open_frecuency_dialog.title') }) as FrequencyDialogResult;
	return result;
}

async function openSymmetricalDialog(signals: string[]): Promise<SymmetricalDialogResult> {
	const result = await modal.showModal(SymmetricalComponentsModal, { signals: signals }, { width: 600, height: 450, title: i18n.i18n.t('signal_viewer.dialogs.open_symmetrical_dialog.title') }) as SymmetricalDialogResult;
	return result;
}

async function openCalculatedDialog(signals: { name: string, isPeriodic: boolean }[]): Promise<CalculatedSignalDialogResult> {
	const result = await modal.showModal(CalculatedSignalModal, { signals: signals }, { width: 500, height: 350, title: i18n.i18n.t('signal_viewer.dialogs.open_calculated_dialog.title') }) as CalculatedSignalDialogResult;
	return result;
}

async function openSpectrumDialog(): Promise<any> {
	await modal.showModal(FrequencySpectrumModal, {}, { width: 600, height: 380, title: i18n.i18n.t('signal_viewer.dialogs.open_spectrum_dialog.title') });
}

async function openSettingsDialog(): Promise<SettingsDialogResult> {
	return await modal.showModal(SettingsModal, {}, { width: 500, height: 600, title: i18n.i18n.t('signal_viewer.dialogs.open_settings_dialog.title') }) as SettingsDialogResult;
}

function showSpinner() {
	loadingId = showLoadingPanel();
}

function hideSpinner() {
	hideLoadingPanel(loadingId);
}

async function showAlert(message: string, icon?: messageBoxIcon) {
	await modal.messageBox(message, i18n.i18n.t('signal_viewer.alerts.title'), "ok", icon ?? "warning");
}

async function descargarFicherosMedida(e: ClickEvent): Promise<void> {
	descargarZipFicherosMedida(id.value!);
}
</script>


<style lang="scss">
.nombre-fichero {
	padding-top: var(--spacing-md);
	display: block;
	font-size: var(--font-size-body-lg);
	color: var(--text-primary-default);
	margin-bottom: 0;
	border-bottom: 1px solid var(--border-default-default)
}

.button-faultan {
	background: #1a1a1a;
	border: 0;
	border-radius: 2px;
	color: #fff;
	font-family: Arial, sans-serif;
	font-weight: 600;
	letter-spacing: .046875em;
	line-height: 1;
	padding: .84375em .875em .78125em;
	text-transform: uppercase
}

.osc_input {
	width: 0;
	margin: 0 -10px -10px 0
}

.osc_label {
	margin: 0 -10px 0 4px
}

.button-faultan:hover {
	background: #007acc
}

.fft_selector {
	margin: .5rem !important;
	padding: .2rem .3rem !important;
	line-height: 1.5;
	border-radius: .25rem !important
}

.fft_input {
	margin: .5rem !important;
	width: 50px !important;
	padding: .2rem .3rem !important;
	line-height: 1.5;
	background-clip: padding-box;
	border: 1px solid #ced4da !important;
	border-radius: .25rem !important
}

.grid {
	display: grid;
	grid-template-columns: 10% 90% 0;
	grid-gap: 0;
	padding: 35px 0 0
}

.grid>div {
	padding: 0;
	text-align: left
}

.div_header {
	height: 7px
}

.div_info_analog {
	font-size: 14px;
	height: 149.5px;
	text-align: left;
	box-sizing: border-box;
	border-bottom-style: solid;
	border-bottom-width: 1px;
	border-bottom-color: var(--border-default-default);
	display: flex;
	align-items: center;
	font-weight: 600;
	color: var(--text-primary-default)
}

.div_info_discrete {
	height: 49.95px;
	text-align: left;
	box-sizing: border-box;
	padding: 8px 2px;
	border-bottom-style: solid;
	border-bottom-width: 1px;
	border-bottom-color: var(--border-default-default);
	color: var(--text-primary-default);
	font-size: 14px;
	font-weight: 600;
}

.buttons_panel {
	position: sticky !important;
	top: 0 !important;
	margin: 0 auto !important;
	height: 32px !important;
	width: 100% !important;
	z-index: 2 !important;
	background-color: #fff !important
}

.buttons_osc {
	float: left !important;
	margin: 0 auto !important;
	height: 32px !important
}

.osc_button_img {
	height: 24px !important;
	width: 24px !important;
	margin: 0 0 0 -4px !important;
	max-width: 24px;
	max-height: 24px
}

.osc_button {
	right: 30px !important;
	height: 32px !important;
	width: 32px !important;
	background-color: #fff !important;
	border: solid #bababa;
	padding: 0;
}

.osc_button:hover {
	background-color: #bababa
}

.osc_button_active {
	right: 30px !important;
	height: 32px !important;
	width: 32px !important;
	border: solid #bababa;
	background-color: #bababa
}

.osc_button_disabled {
	right: 30px !important;
	height: 32px !important;
	width: 32px !important;
	border: solid #ededed;
	background-color: #ededed
}

.loader {
	border: 5px solid #f3f3f3;
	border-top: 5px solid #555;
	border-radius: 50%;
	width: 50px;
	height: 50px;
	animation: spin 1s linear infinite
}

@keyframes spin {
	0% {
		transform: rotate(0deg)
	}

	100% {
		transform: rotate(360deg)
	}
}

.settings_button {
	border-bottom: #fff;
	border: none;
	width: 90%;
	text-align: left;
	height: 30px;
	padding: 0 0 0 4px
}

a[href='https://plotly.com/'] {
	display: none;
}

.datos-senales {
	padding: var(--spacing-sm)
}

.div_osc_tool input[type="checkbox"] {
	margin-right: 8px;
}
</style>